import React, { useEffect, Component } from "react";

import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Pipeline from "@/pages/Pipeline";
import Properties from "@/pages/Properties";
import Launches from "@/pages/Launches";
import LaunchDetail from "@/pages/LaunchDetail";
import Advantage from "@/pages/Advantage";
import Campaigns from "@/pages/Campaigns";
import Settings from "@/pages/Settings";
import PublicShortlist from "@/pages/PublicShortlist";
import AppLayout from "@/components/AppLayout";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#F5F3EE] text-[#0A1128] flex flex-col items-center justify-center p-6 text-center space-y-4">
          <h1 className="font-serif-display text-3xl font-medium">Alpine Straits Property Partners</h1>
          <p className="text-sm text-slate-600 max-w-md">
            Something went wrong while displaying this page. Please refresh or return home.
          </p>
          {this.state.error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-4 rounded-lg max-w-xl text-left overflow-auto font-mono max-h-48">
              <div className="font-bold mb-1">{String(this.state.error.name || "Error")}: {String(this.state.error.message || this.state.error)}</div>
              {this.state.errorInfo?.componentStack && (
                <div className="text-[10px] text-red-400 mt-2 whitespace-pre-wrap">
                  {String(this.state.errorInfo.componentStack)}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null, errorInfo: null });
              window.location.href = "/";
            }}
            className="px-5 py-2.5 bg-[#0A1128] text-white text-xs font-semibold rounded-lg hover:bg-[#152243] transition-colors"
          >
            Refresh & Return Home
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-slate-500">Loading…</div>;
  if (!user && !location.state?.user) return <Navigate to="/" replace />;
  return children;
};

const AppRouter = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Alpine Straits - Your Singapore Property Partners";
  }, [location]);

  // Detect Emergent OAuth callback synchronously during render

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/advantage" element={<Advantage />} />
      <Route path="/news" element={<Navigate to="/#insights" replace />} />
      <Route path="/news/:slug" element={<Navigate to="/#insights" replace />} />
      <Route path="/insights" element={<Navigate to="/#insights" replace />} />
      <Route path="/launches" element={<Launches />} />
      <Route path="/launches/:slug" element={<LaunchDetail />} />
      <Route path="/shortlist/:token" element={<PublicShortlist />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="properties" element={<Properties />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
